import dayjs from 'dayjs';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import SummaryCard from 'components/charts/SummaryCard';
import DoughnutChart from 'components/charts/chartJS/DoughnutChart';
import EmptyChart from 'components/charts/chartJS/EmptyChart';
import { Legend, LegendEntryTitle, LegendLine } from 'components/charts/chartJS/styled/CustomChartLegend.styled';
import { chartColors } from 'components/charts/chartJS/utils';
import { Divider } from 'components/charts/styled/Chart.styled';
import DateFilter from 'components/field/DateFilter';
import Card from 'components/global/Card';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { TwoCardsInRow } from 'pages/Dashboard/Dashboard.styled';

import { REPORT } from 'services/consts';

import { colorCombinations } from 'utils/colors';
import PATHS, { mergePaths } from 'utils/paths';

import { faChartLineUp, faSackDollar } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';

import * as S from './ShippingAccounting.styled';

const ShippingAccountingTab = () => (
	<WidgetPageContainer>
		<WidgetWrapper widgetId={'shippingAccountingSummary'}>
			<Summary />
		</WidgetWrapper>
		<WidgetWrapper widgetId={'otoShippingPartners'}>
			<ChartHeader title={'otoShippingPartners'} showDivider={false} gutterBottom />
			<TwoCardsInRow>
				<ShippingFees />
				<CodOto />
			</TwoCardsInRow>
		</WidgetWrapper>
		<WidgetWrapper widgetId={'yourShippingPartners'}>
			<Divider />
			<ChartHeader title={'yourShippingPartners'} showDivider={false} gutterBottom />
			<TwoCardsInRow>
				<DCShippingFees />
				<OTOFees />
				<CODCollected />
			</TwoCardsInRow>
		</WidgetWrapper>
	</WidgetPageContainer>
);

const Summary = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DELIVERY_COMPANY.GET_TOTAL_ANALYSIS,
		body
	});

	const cardsConfig = [
		{
			icon: faSackDollar,
			headerLabel: 'totalShippingFees',
			prefix: 'totalShippingFees',
			dataKey: 'total',
			details: [
				{
					label: 'totalShippingFeesOtoShippingPartnes',
					dataKey: 'otoShippingPartners'
				},
				{
					label: 'totalShippingFeesYourShippingPartnes',
					dataKey: 'yourShippingPartners'
				}
			]
		},
		{
			icon: faChartLineUp,
			headerLabel: 'averageCost',
			prefix: 'averages',
			dataKey: 'total',
			details: [
				{
					label: 'averageCostOtoShippingPartners',
					dataKey: 'otoShippingPartners'
				},
				{
					label: 'averageCostYourShippingPartners',
					dataKey: 'yourShippingPartners'
				}
			]
		}
	];

	return (
		<>
			<ChartHeader
				title={'shippingAccountingSummary'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
				showDivider={false}
			/>
			<TwoCardsInRow ref={ref}>
				{cardsConfig.map((card, index) => (
					<SummaryCard key={index} data={data} loading={loading} card={card} />
				))}
			</TwoCardsInRow>
		</>
	);
};

const afterFetch = (data, t) => ({
	labels: data.map((item) => t(item.label)),
	datasets: [
		{
			data: data.map((item) => item.value || 0),
			backgroundColor: chartColors.backgroundColor,
			borderColor: 'white'
		}
	],
	currency: data?.[0]?.currency
});

export const ShippingFees = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const [selectedColumn, setSelectedColumn] = useState('total');

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DELIVERY_COMPANY.GET_OTO_SHIPPING_PARTNER_FEES,
		body
	});

	const [parsedData, setParsedData] = useState([]);

	useEffect(() => {
		if (data) {
			const parsedData = {
				labels: data.map((item) => item.deliveryCompanySettingsName),
				datasets: [
					{
						data: data.map((item) => item[selectedColumn] || 0),
						backgroundColor: chartColors.backgroundColor,
						borderColor: 'white'
					}
				]
			};
			setParsedData(parsedData);
		}
	}, [data, selectedColumn]);

	return (
		<Card ref={ref}>
			<ChartHeader
				title={'shippingFees'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
				titleTooltip={'analysis.shippingFeesTooltip'}
			/>
			<DoughnutChart
				data={parsedData}
				loading={loading}
				showLegend={false}
				innerTitle={selectedColumn === 'total' ? 'shippingFees' : 'avgCost'}
				emptyChartData={{
					subtitle: 'otoShippingPartnersEmptyChartEmptyStateSubtitle'
				}}
				withCurrency={true}
				currency={data?.[0]?.currency}
			/>
			{!loading && data && data.length > 0 && (
				<S.Container>
					<Legend legendPlacement={'bottom'}>
						<S.LegendHeaderLine>
							<Typography noWrap color={'var(--content-3)'}>
								deliveryCompany
							</Typography>
							<S.LegendHeader>
								<Typography
									noWrap
									color={selectedColumn === 'total' ? 'var(--blue)' : 'var(--content-3)'}
									onClick={() => setSelectedColumn('total')}
									className="text-end">
									shippingFees
								</Typography>
							</S.LegendHeader>
							<S.LegendHeader>
								<Typography
									noWrap
									color={selectedColumn === 'average' ? 'var(--blue)' : 'var(--content-3)'}
									onClick={() => setSelectedColumn('average')}
									className="text-end">
									avgCost
								</Typography>
							</S.LegendHeader>
						</S.LegendHeaderLine>
						{data?.map((entry, index) => (
							<LegendLine key={index}>
								<LegendEntryTitle>
									<Icon
										icon={faCircle}
										color={colorCombinations[index % colorCombinations.length]?.color}
									/>
									<Typography noWrap color={'var(--content-3)'}>
										{entry.deliveryCompanySettingsName}
									</Typography>
								</LegendEntryTitle>
								<Typography
									variant="p3"
									className="text-end"
									noWrap
									fontWeight="700"
									withCurrency={true}
									currency={data?.[0]?.currency}>
									{entry.total}
								</Typography>
								<Typography
									variant="p3"
									className="text-end"
									noWrap
									fontWeight="700"
									withCurrency={true}
									currency={data?.[0]?.currency}>
									{entry.average}
								</Typography>
							</LegendLine>
						))}
					</Legend>
				</S.Container>
			)}
		</Card>
	);
};

const CodOto = () => {
	const navigate = useNavigate();
	return (
		<Card>
			<ChartHeader title={'COD'} titleTooltip={'analysis.codTootlip'} />
			<EmptyChart
				subtitle={'checkCodWalletDescription'}
				imageUrl={'https://storage.googleapis.com/tryoto-public/public/assets/images/cod.svg'}
				buttonLabel={'checkCodWallet'}
				buttonAction={() => navigate(PATHS.COD_WALLET)}
			/>
		</Card>
	);
};

export const DCShippingFees = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SHIPPING_ACCOUNTING.GET_SAAS_FEES,
		body
	});

	const [parsedData, setParsedData] = useState([]);
	const [selectedColumn, setSelectedColumn] = useState('total');
	const navigate = useNavigate();

	useEffect(() => {
		if (data) {
			const parsedData = {
				labels: data.map((item) => item.deliveryCompanySettingsName),
				datasets: [
					{
						data: data.map((item) => item[selectedColumn] || 0),
						backgroundColor: chartColors.backgroundColor,
						borderColor: 'white'
					}
				]
			};
			setParsedData(parsedData);
		}
	}, [data, selectedColumn]);

	return (
		<Card ref={ref}>
			<ChartHeader title={'DCFees'} filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />} />
			<DoughnutChart
				data={parsedData}
				loading={loading}
				showLegend={false}
				innerTitle={selectedColumn === 'total' ? 'shippingFees' : 'avgCost'}
				emptyChartData={{
					subtitle: 'dcFeesEmptyChartEmptyStateSubtitle',
					buttonLabel: 'dcFeesEmptyChartEmptyStateButtonLabel',
					buttonAction: () => navigate(mergePaths(PATHS.SHIPPING_PRICE, PATHS.SHIPPING_PRICES))
				}}
				withCurrency={true}
				currency={data?.[0]?.currency}
			/>
			{!loading && data && data.length > 0 && (
				<S.Container>
					<Legend legendPlacement={'bottom'}>
						<S.LegendHeaderLine>
							<Typography noWrap color={'var(--content-3)'}>
								deliveryCompany
							</Typography>
							<S.LegendHeader>
								<Typography
									noWrap
									color={selectedColumn === 'total' ? 'var(--blue)' : 'var(--content-3)'}
									onClick={() => setSelectedColumn('total')}
									className="text-end">
									shippingFees
								</Typography>
							</S.LegendHeader>
							<S.LegendHeader>
								<Typography
									noWrap
									color={selectedColumn === 'average' ? 'var(--blue)' : 'var(--content-3)'}
									onClick={() => setSelectedColumn('average')}
									className="text-end">
									avgCost
								</Typography>
							</S.LegendHeader>
						</S.LegendHeaderLine>
						{data?.map((entry, index) => (
							<LegendLine key={index}>
								<LegendEntryTitle>
									<Icon
										icon={faCircle}
										color={colorCombinations[index % colorCombinations.length]?.color}
									/>
									<Typography noWrap color={'var(--content-3)'}>
										{entry.deliveryCompanySettingsName}
									</Typography>
								</LegendEntryTitle>
								<Typography
									variant="p3"
									className="text-end"
									noWrap
									fontWeight="700"
									withCurrency={true}
									currency={data?.[0]?.currency}>
									{entry.total}
								</Typography>
								<Typography
									variant="p3"
									className="text-end"
									noWrap
									fontWeight="700"
									withCurrency={true}
									currency={data?.[0]?.currency}>
									{entry.average}
								</Typography>
							</LegendLine>
						))}
					</Legend>
				</S.Container>
			)}
		</Card>
	);
};

export const OTOFees = () => {
	const { t } = useTranslation();
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const afterFetchOTOFees = (data) => ({
		labels: data.map((item) => t(item.deliveryCompanySettingsName)),
		datasets: [
			{
				data: data.map((item) => item.total || 0),
				backgroundColor: chartColors.backgroundColor,
				borderColor: 'white'
			}
		],
		currency: data?.[0]?.currency
	});

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SHIPPING_ACCOUNTING.GET_INTEGRATOR_DELIVERY_FEES,
		body,
		parseData: (data) => afterFetchOTOFees(data)
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={'OTOFees'} filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />} />
			<DoughnutChart
				data={data}
				loading={loading}
				emptyChartData={{
					subtitle: 'otoFeesEmptyChartEmptyStateSubtitle'
				}}
				withCurrency={true}
				currency={data?.currency}
			/>
		</Card>
	);
};

export const CODCollected = () => {
	const { t } = useTranslation();
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DELIVERY_COMPANY.GET_COD_COLLECTED,
		body,
		parseData: (data) => afterFetch(data, t)
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={'CODCollected'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
			/>
			<DoughnutChart data={data} loading={loading} withCurrency currency={data?.currency} />
		</Card>
	);
};

export default ShippingAccountingTab;
